import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useFormikContractHelpers } from 'hooks';
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ISelectOption } from 'types/components/inputs/input.interface';
import { adjustTranslate, measuring } from 'utils/dnd/dndUtilities';
import ContractAgreementDetailSelectOptionItem from './item/ContractAgreementDetailSelectOptionItem';

interface IContractAgreementDetailSelectOptionsListProps {
  selectOptions: ISelectOption<number>[];
}

const ContractAgreementDetailSelectOptionsList = ({
  selectOptions,
}: IContractAgreementDetailSelectOptionsListProps) => {
  const { setDetailValue, values, setItemAndDetailValue } =
    useFormikContractHelpers();

  const sortedOptionsIds = useMemo(
    () => selectOptions.map(({ value }) => ({ id: value })),
    [selectOptions]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    setDetailValue<UniqueIdentifier>('selectOptionActiveId', active.id);
    document.body.style.setProperty('cursor', 'grabbing');
  };

  const resetState = () => {
    setDetailValue('selectOptionActiveId', undefined);
    document.body.style.setProperty('cursor', '');
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (
      values.sideMenu?.detail?.selectOptionActiveId &&
      active.id !== over.id
    ) {
      const oldIndex = selectOptions.findIndex(
        option => option.value === active.id
      );
      const newIndex = selectOptions.findIndex(
        option => option.value === over.id
      );

      const movedArray = arrayMove(selectOptions, oldIndex, newIndex);

      setItemAndDetailValue<string[]>(
        'content.selectAgreementTemplate.options',
        movedArray.map(option => option.label)
      );

      resetState();
    }
  };

  const removeOption = (id: number) => {
    const filteredArray: string[] = selectOptions
      .filter(option => option.value !== id)
      .map(option => option.label);

    setItemAndDetailValue<string[]>(
      'content.selectAgreementTemplate.options',
      filteredArray
    );

    const defaultValue =
      values.sideMenu?.detail?.content.selectAgreementTemplate?.defaultValue;

    if (defaultValue && !filteredArray.includes(defaultValue)) {
      setItemAndDetailValue<string>(
        'content.selectAgreementTemplate.defaultValue',
        filteredArray.length ? filteredArray[0] : ''
      );
    }

    if (!filteredArray.length) {
      setItemAndDetailValue<string>(
        'content.selectAgreementTemplate.defaultValue',
        ''
      );
    }
  };

  const dropAnimationConfig: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.4',
        },
      },
    }),
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={measuring}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={resetState}>
      <SortableContext
        items={sortedOptionsIds}
        strategy={verticalListSortingStrategy}>
        {selectOptions.map((option, key) => (
          <ContractAgreementDetailSelectOptionItem
            key={key}
            id={option.value}
            removeOption={() => removeOption(option.value)}
          />
        ))}
      </SortableContext>

      {values.sideMenu?.detail?.selectOptionActiveId !== undefined
        ? createPortal(
            <DragOverlay
              dropAnimation={dropAnimationConfig}
              modifiers={[adjustTranslate]}>
              <ContractAgreementDetailSelectOptionItem
                id={values.sideMenu?.detail.selectOptionActiveId}
                removeOption={() =>
                  removeOption(
                    values.sideMenu?.detail?.selectOptionActiveId ?? 0
                  )
                }
              />
            </DragOverlay>,
            document.body
          )
        : undefined}
    </DndContext>
  );
};

export default ContractAgreementDetailSelectOptionsList;
